import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { Navigation } from 'swiper/modules';
import { TemplateContext } from '../../contexts/templates.context';
import { TemplateCategoryEnum } from '../../enums/templateForm.enums';
import ButtonPreview from '../ButtonPreview';
import {
  TemplateButtons,
  BizMTemplateEntity,
} from '../../types/template.types';
import HighlightVars from '../preview/HighlightVars.component';
import { BsArrowRight } from 'react-icons/bs';
import {
  MessageWrap,
  AlimtalkHeader,
  ImgStyled,
  EmphasizeTypeText,
  KakaoMessage,
  SmsMessageWrap,
  ButtonGroup,
  CarouselContainer,
  Extra,
  More,
  TemplateHeader,
  TemplateItem,
  TemplateWideItem,
  VideoContainer,
  Poster,
  Notice,
  Product,
  Price,
} from './Message.style';

type MessageContainerProps = {
  activeTab?: number;
  hideLogoHandle?: (value: boolean) => void;
};

export type NavigationHandle = {
  handlePrev: () => void;
  handleNext: () => void;
};

const isCustomVariable = (value: unknown): boolean => {
  return typeof value === 'string' && /^#\{[A-Za-z0-9_]+\}$/.test(value);
};

const MessageContainer = forwardRef<NavigationHandle, MessageContainerProps>(
  ({ activeTab = 0, hideLogoHandle }, ref) => {
    const { currentTemplateData, selectedTemplateCategory } =
      useContext(TemplateContext);

    const normalizedTemplateCategory =
      currentTemplateData?.templateCategory === TemplateCategoryEnum.FRIENDTALK
        ? TemplateCategoryEnum.BRANDMESSAGE
        : currentTemplateData?.templateCategory;

    const {
      message,
      smsMessage,
      smsImage,
      templateExtra,
      templateHeader,
      templateEmphasizeType,
      templateTitle,
      templateSubtitle,
      templateImageUrl,
      smsTitle,
      templateItemHighlight,
      templateItem,
      summary,
      carousel,
      templateWideItem,
      messageType,
      fallback,
      video,
      commerce,
      additionalContent,
    } = currentTemplateData ?? {};

    const [swiper, setSwiper] = useState<SwiperCore>();
    const [buttons, setButtons] = useState<TemplateButtons[]>([]);
    const [isKakaoMessage, setIsKakaoMessage] = useState(false);
    const [videoId, setVideoId] = useState<string>();
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    const playVideo = () => {
      setIsVideoPlaying(!isVideoPlaying);
    };

    useImperativeHandle(ref, () => ({
      handlePrev() {
        if (swiper) {
          swiper.slidePrev();
        }
      },

      handleNext() {
        if (swiper) {
          swiper.slideNext();
        }
      },
    }));

    useEffect(() => {
      if (swiper) {
        swiper.slideTo(activeTab, 500);
      }
    }, [swiper, activeTab]);

    useEffect(() => {
      if (swiper && currentTemplateData) {
        swiper.slideTo(0, 0); // slide instantly to first slide
      }
    }, [currentTemplateData, swiper]);

    useEffect(() => {
      if (!currentTemplateData) {
        return;
      }
      setVideoId('');
      setButtons([]);
      const buttonsArr = [] as TemplateButtons[];
      for (let i = 1; i <= 5; ++i) {
        const buttonNum = `button${i}` as keyof BizMTemplateEntity;
        if (currentTemplateData[buttonNum] === undefined) break;
        buttonsArr.push(currentTemplateData[buttonNum] as TemplateButtons);
      }
      setButtons(buttonsArr);

      if (video?.video_url) {
        const id = video.video_url.split('/').pop();
        if (id?.includes('?')) {
          const idWithoutParams = id?.substring(0, id.indexOf('?'));
          setVideoId(idWithoutParams);
        } else {
          setVideoId(id);
        }
      }
    }, [currentTemplateData, video]);

    useEffect(() => {
      if (!selectedTemplateCategory) return;
      setIsKakaoMessage(
        selectedTemplateCategory.value === TemplateCategoryEnum.ALIMTALK ||
          selectedTemplateCategory.value === TemplateCategoryEnum.FRIENDTALK ||
          selectedTemplateCategory.value === TemplateCategoryEnum.BRANDMESSAGE,
      );
    }, [selectedTemplateCategory]);

    if (!selectedTemplateCategory) return <></>;
    return (
      <>
        <MessageWrap
          $carousel={messageType === 'FC' || messageType === 'FA'}
          $isfriendtalk={
            selectedTemplateCategory.value === 'FRIENDTALK' ||
            selectedTemplateCategory.value === 'BRANDMESSAGE'
          }
          className={isKakaoMessage ? 'isKakaoMessage-noDashboard' : ''}
        >
          {((normalizedTemplateCategory === TemplateCategoryEnum.ALIMTALK &&
            selectedTemplateCategory.value !== 'SMS') ||
            selectedTemplateCategory.value ===
              TemplateCategoryEnum.ALIMTALK) && (
            <AlimtalkHeader>Info-talk</AlimtalkHeader>
          )}

          {templateImageUrl &&
            selectedTemplateCategory.value === normalizedTemplateCategory &&
            selectedTemplateCategory.value !== 'SMS' && (
              <ImgStyled
                src={templateImageUrl}
                alt="Header image"
                className={
                  normalizedTemplateCategory ===
                    TemplateCategoryEnum.ALIMTALK ||
                  selectedTemplateCategory.value ===
                    TemplateCategoryEnum.ALIMTALK
                    ? 'ATImage'
                    : ''
                }
              />
            )}
          {isKakaoMessage && messageType === 'FP' && videoId && (
            <VideoContainer>
              <iframe
                width="100%"
                height="auto"
                src={`https://play-tv.kakao.com/embed/player/cliplink/${videoId}?autoplay=${
                  isVideoPlaying ? 1 : 0
                }`}
                allow="autoplay; fullscreen; encrypted-media;"
              ></iframe>

              {!isVideoPlaying && video && video.thumbnail_url && (
                <Poster $image={video.thumbnail_url} onClick={playVideo} />
              )}
            </VideoContainer>
          )}
          {isKakaoMessage &&
            (templateEmphasizeType === 'ITEM_LIST' ||
              messageType === 'FL' ||
              messageType === 'FP') &&
            templateHeader && (
              <TemplateHeader $type={messageType}>
                {HighlightVars(templateHeader)}
              </TemplateHeader>
            )}

          {isKakaoMessage &&
            templateEmphasizeType === 'ITEM_LIST' &&
            templateItemHighlight && (
              <TemplateHeader>
                <div>
                  <div className="title">
                    {HighlightVars(templateItemHighlight.title)}
                  </div>
                  <div className="desc">
                    {HighlightVars(templateItemHighlight.description)}
                  </div>
                </div>
                {templateItemHighlight.imageUrl && (
                  <img
                    src={templateItemHighlight.imageUrl}
                    className="item-highlight-img"
                  />
                )}
              </TemplateHeader>
            )}

          {isKakaoMessage &&
            templateEmphasizeType === 'ITEM_LIST' &&
            templateItem && (
              <TemplateItem>
                <ul>
                  {templateItem.list.map((item, index) => {
                    return (
                      <li key={`templateItem-list-item-${index}`}>
                        <span className="label">{item.title}</span>
                        <span>{HighlightVars(item.description)}</span>
                      </li>
                    );
                  })}
                </ul>
                {summary && (
                  <ul>
                    <li>
                      <span className="label">{summary.title}</span>
                      <strong>{HighlightVars(summary.description)}</strong>
                    </li>
                  </ul>
                )}
                {templateItem.summary && (
                  <ul>
                    <li>
                      <span className="label">
                        {templateItem.summary.title}
                      </span>
                      <strong>
                        {HighlightVars(templateItem.summary.description)}
                      </strong>
                    </li>
                  </ul>
                )}
              </TemplateItem>
            )}

          {templateWideItem && isKakaoMessage && (
            <TemplateWideItem>
              <ul>
                {templateWideItem?.map((item, index) => {
                  return (
                    item.img_url && (
                      <li key={`templateWideItem-list-item-${index}`}>
                        <img src={item.img_url} />
                        <span className="label">{item.title}</span>
                      </li>
                    )
                  );
                })}
              </ul>
            </TemplateWideItem>
          )}

          {isKakaoMessage && messageType === 'FM' && commerce && (
            <Product>
              <div className="name">{commerce.title}</div>
              <Price>
                {commerce.discount_price && (
                  <div className="discount">
                    {isCustomVariable(commerce.discount_price)
                      ? HighlightVars(commerce.discount_price as string)
                      : new Intl.NumberFormat('ko-KR').format(
                          commerce.discount_price as number,
                        )}
                    원
                  </div>
                )}
                {commerce.regular_price && (
                  <div
                    className={
                      !commerce.discount_price ? 'discount' : 'regular'
                    }
                  >
                    {isCustomVariable(commerce.regular_price)
                      ? HighlightVars(commerce.regular_price as string)
                      : new Intl.NumberFormat('ko-KR').format(
                          commerce.regular_price as number,
                        )}
                    원
                  </div>
                )}
                {commerce.discount_rate && (
                  <div className="rate">
                    {HighlightVars(commerce.discount_rate.toString())}%
                  </div>
                )}
                {commerce.discount_fixed && (
                  <div className="rate fixed">
                    {isCustomVariable(commerce.discount_fixed)
                      ? HighlightVars(commerce.discount_fixed as string)
                      : new Intl.NumberFormat('ko-KR').format(
                          commerce.discount_fixed as number,
                        )}
                    원
                  </div>
                )}
              </Price>
            </Product>
          )}

          {isKakaoMessage &&
            templateEmphasizeType === 'TEXT' &&
            templateTitle && (
              <EmphasizeTypeText>
                <div className="emphSubTitle">{templateSubtitle}</div>
                <div className="emphTitle">{HighlightVars(templateTitle)}</div>
              </EmphasizeTypeText>
            )}
          {isKakaoMessage && messageType !== 'FL' && (
            <>
              <KakaoMessage
                $carousel={messageType === 'FC' || messageType === 'FA'}
              >
                {message && HighlightVars(message)}
              </KakaoMessage>
              {templateExtra && <Extra>{templateExtra}</Extra>}
            </>
          )}
          {isKakaoMessage && messageType === 'FM' && (
            <>{additionalContent && <Extra>{additionalContent}</Extra>}</>
          )}
          {selectedTemplateCategory.value !== normalizedTemplateCategory &&
            fallback && (
              <>
                {fallback.smsTitle && (
                  <SmsMessageWrap className="sms-title">
                    <div>{HighlightVars(fallback.smsTitle)}</div>
                  </SmsMessageWrap>
                )}
                {fallback.smsImage && (
                  <ImgStyled src={fallback.smsImage} className="smsImage" />
                )}
                {fallback.smsMessage && (
                  <SmsMessageWrap className="last-message">
                    <div className="desc">
                      {HighlightVars(fallback.smsMessage)}
                    </div>
                  </SmsMessageWrap>
                )}
              </>
            )}
          {selectedTemplateCategory.value === TemplateCategoryEnum.SMS &&
            normalizedTemplateCategory === TemplateCategoryEnum.SMS && (
              <>
                {smsTitle && (
                  <SmsMessageWrap className="sms-title">
                    <div>{HighlightVars(smsTitle)}</div>
                  </SmsMessageWrap>
                )}
                {smsImage && <ImgStyled src={smsImage} className="smsImage" />}
                {smsMessage && (
                  <SmsMessageWrap className="last-message">
                    <div className="desc">{HighlightVars(smsMessage)}</div>
                  </SmsMessageWrap>
                )}
              </>
            )}
          {isKakaoMessage && messageType !== 'FC' && buttons && (
            <ButtonGroup
              className={
                messageType === 'FL' || messageType === 'FM' ? 'inline' : ''
              }
            >
              {buttons.map((button, index) => (
                <ButtonPreview
                  key={`kakao-btn-${button.name}-${index}`}
                  text={button.name}
                  type={button.type as string}
                  buttonData={button}
                />
              ))}
            </ButtonGroup>
          )}
          {isKakaoMessage && carousel?.list && (
            <CarouselContainer>
              <Swiper
                modules={[Navigation]}
                slidesPerView={1.4}
                spaceBetween={5}
                autoHeight={false}
                onSwiper={setSwiper}
                onAfterInit={(swiper: SwiperCore) => {
                  const index = swiper.activeIndex;
                  if (hideLogoHandle) {
                    hideLogoHandle(index !== 0);
                  }
                }}
                onSlideChange={(swiper: SwiperCore) => {
                  const index = swiper.activeIndex;
                  const length = swiper.slides.length;
                  swiper.allowSlideNext = index !== length - 2;
                  if (hideLogoHandle) {
                    hideLogoHandle(index !== 0);
                  }
                }}
              >
                {carousel.head && (
                  <SwiperSlide key="head-slide">
                    <img src={carousel.head.image_url} />
                    <div className="content carouselHead">
                      <div className="title">{carousel.head.header}</div>
                      <div className="desc">
                        {HighlightVars(carousel.head.content)}
                      </div>
                    </div>
                  </SwiperSlide>
                )}
                {carousel.list.map((slide, index) => {
                  return (
                    <SwiperSlide key={`carousel-list-slide-${index}`}>
                      {slide.image && slide.image.imageUrl && (
                        <img src={slide.image.imageUrl} />
                      )}
                      <div className="content">
                        {slide.header && (
                          <div className="title">{slide.header}</div>
                        )}
                        {slide.message && (
                          <div className="desc">
                            {HighlightVars(slide.message)}
                          </div>
                        )}
                        {slide.commerce && (
                          <>
                            <Product $messageType={messageType}>
                              <div className="name">{slide.commerce.title}</div>
                              <Price>
                                {slide.commerce.discount_price && (
                                  <div className="discount">
                                    {isCustomVariable(
                                      slide.commerce.discount_price,
                                    )
                                      ? HighlightVars(
                                          slide.commerce
                                            .discount_price as string,
                                        )
                                      : new Intl.NumberFormat('ko-KR').format(
                                          slide.commerce
                                            .discount_price as number,
                                        )}{' '}
                                    원
                                  </div>
                                )}
                                {slide.commerce.regular_price && (
                                  <div
                                    className={
                                      !slide.commerce.discount_price
                                        ? 'discount'
                                        : 'regular'
                                    }
                                  >
                                    {isCustomVariable(
                                      slide.commerce.regular_price,
                                    )
                                      ? HighlightVars(
                                          slide.commerce
                                            .regular_price as string,
                                        )
                                      : new Intl.NumberFormat('ko-KR').format(
                                          slide.commerce
                                            .regular_price as number,
                                        )}
                                    원
                                  </div>
                                )}
                                {slide.commerce.discount_rate && (
                                  <div className="rate">
                                    {HighlightVars(
                                      slide.commerce.discount_rate.toString(),
                                    )}
                                    %
                                  </div>
                                )}
                                {slide.commerce.discount_fixed && (
                                  <div className="rate fixed">
                                    {isCustomVariable(
                                      slide.commerce.discount_fixed,
                                    )
                                      ? HighlightVars(
                                          slide.commerce
                                            .discount_fixed as string,
                                        )
                                      : new Intl.NumberFormat('ko-KR').format(
                                          slide.commerce
                                            .discount_fixed as number,
                                        )}
                                    원
                                  </div>
                                )}
                              </Price>
                            </Product>
                            {slide.additionalContent && (
                              <Extra>{slide.additionalContent}</Extra>
                            )}
                          </>
                        )}
                      </div>
                      <div className="inlineButtons nogap">
                        {slide.buttons &&
                          slide.buttons.map((button, idx) => (
                            <ButtonPreview
                              key={`carousel-list-slide-btn-${button.name}-${idx}`}
                              text={button.name}
                              type={button.type as string}
                              buttonData={button}
                            />
                          ))}
                      </div>
                    </SwiperSlide>
                  );
                })}
                {carousel.tail && (
                  <SwiperSlide className="more">
                    <More>
                      <BsArrowRight size="21px" />
                      <span>더보기</span>
                    </More>
                  </SwiperSlide>
                )}
                <SwiperSlide />
              </Swiper>
            </CarouselContainer>
          )}
        </MessageWrap>
        {messageType === 'FP' && (
          <Notice>
            Video preview may slightly differ from real sent message.
          </Notice>
        )}
      </>
    );
  },
);

MessageContainer.displayName = 'MessageContainer';
export default MessageContainer;
